import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { CreditCard, Mic, ArrowUpRight, Sparkles } from "lucide-react";

const PLATFORMS = [
  {
    key: "vcard",
    Icon: CreditCard,
    domain: "https://leadcard.bicenter-ai.com",
    accent: "from-bicenter-cyan/25 to-bicenter-blue/0",
    border: "border-bicenter-cyan/40",
    badge_fr: "Nouveau",
    badge_en: "New",
  },
  {
    key: "speaker",
    Icon: Mic,
    domain: "https://speaker.bicenter-ai.com",
    accent: "from-bicenter-violet/25 to-bicenter-magenta/0",
    border: "border-bicenter-magenta/40",
    badge_fr: "Nouveau",
    badge_en: "New",
  },
];

export const EcosystemExtras = () => {
  const { t, lang } = useLanguage();
  const labels = t.ecosystem;

  return (
    <section id="ecosystem" className="relative py-20 md:py-24" data-testid="ecosystem-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 right-1/4 w-[400px] h-[400px] rounded-full bg-bicenter-cyan/8 blur-[160px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-sm mb-6">
            <Sparkles size={12} className="text-bicenter-cyan" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-300">{labels.badge}</span>
          </div>
          <h3 className="font-display text-3xl md:text-4xl font-light tracking-tight text-white leading-[1.1]">
            {labels.title}
          </h3>
          <p className="mt-4 text-zinc-400 font-body leading-relaxed">
            {labels.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {PLATFORMS.map((p) => {
            const info = labels.platforms[p.key];
            const Icon = p.Icon;
            return (
              <a
                key={p.key}
                href={p.domain}
                target="_blank"
                rel="noreferrer"
                data-testid={`ecosystem-platform-${p.key}`}
                className={`group relative block bg-[#0c0c0e] border ${p.border} rounded-3xl p-8 hover:-translate-y-1 transition-all duration-500 overflow-hidden`}
              >
                <div className={`absolute -top-20 -right-20 w-60 h-60 rounded-full bg-gradient-to-br ${p.accent} blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700`}></div>

                <div className="relative flex items-start justify-between gap-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${p.accent} border ${p.border} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={22} className="text-white" />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.28em] px-3 py-1 rounded-full border border-bicenter-cyan/30 text-bicenter-cyan bg-bicenter-cyan/5">
                    {lang === "fr" ? p.badge_fr : p.badge_en}
                  </span>
                </div>

                <div className="relative mt-6">
                  <h4 className="font-display text-2xl md:text-3xl font-medium text-white tracking-tight">
                    {info.name}
                  </h4>
                  <p className="mt-2 text-sm text-zinc-400">
                    {info.tagline}
                  </p>
                  <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
                    {info.description}
                  </p>
                </div>

                <div className="relative mt-6 flex items-center justify-between">
                  <p className="text-xs text-zinc-500 font-mono">
                    {p.domain.replace("https://", "")}
                  </p>
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-white border border-white/10 rounded-full px-4 py-2 group-hover:border-white/30 group-hover:bg-white/5 transition-all">
                    {labels.cta}
                    <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
